"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, CircleCheck as CheckCircle2, Clock, Sparkles, UserCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
interface ServiceLinkItem {
  readonly title: string;
  readonly to: string;
  readonly badge: string;
  readonly description: string;
  readonly timeframe: string;
  readonly highlight: string;
}
const serviceItems: readonly ServiceLinkItem[] = [{
  title: "Hire SEO Consultant",
  to: "/HireSeoConsultantPage",
  badge: "Direct Advisory",
  description: "Work directly with me for bespoke search audits, strategic roadmap creation, and hands-on execution for your key commercial terms.",
  timeframe: "Flexible project sprints",
  highlight: "Direct 1-on-1 collaboration"
}, {
  title: "Monthly SEO Services",
  to: "/MonthlySeoServicesPage",
  badge: "Continuous Growth",
  description: "Retain me as your dedicated SEO specialist month over month to expand organic footprint, resolve technical blockers, and build authority.",
  timeframe: "Ongoing monthly retainer",
  highlight: "Transparent monthly deliverables"
}, {
  title: "Fractional SEO Consultant",
  to: "/FractionalSeoConsultantPage",
  badge: "Leadership & Strategy",
  description: "Embed senior search leadership into your leadership team without the full-time executive cost. Guide in-house devs and writers with precision.",
  timeframe: "Part-time embedded advisory",
  highlight: "Senior leadership integration"
}];
export default function ConsultancyLinks() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="consultancyserviceslinks" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header content */}
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-18">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="border-border bg-secondary text-secondary-foreground px-3 py-1 text-xs uppercase tracking-wide font-medium">
              Consultancy Models
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-foreground tracking-tight mb-4">
            Choose how you want to work with me
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Direct, senior-level SEO consultancy without agency account managers, markups, or junior hand-offs. You partner directly with me from day one.
          </p>
        </div>

        {/* Services Links Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {serviceItems.map((service, i) => <motion.div key={service.title} data-index={i} initial={prefersReducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          delay: prefersReducedMotion ? 0 : i * 0.12
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md hover:border-primary/40 transition-[border-color,box-shadow] duration-300 rounded-xl">
                <div>
                  <CardHeader className="p-6 pb-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <span className="inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                        {service.badge}
                      </span>
                      <div className="flex items-center text-xs text-muted-foreground gap-1">
                        <Clock className="size-3.5 text-muted-foreground" />
                        <span>{service.timeframe}</span>
                      </div>
                    </div>
                    <CardTitle className="text-xl sm:text-2xl font-serif font-semibold text-foreground">
                      <Link to={service.to} className="hover:text-primary transition-colors inline-flex items-center gap-2 group">
                        <span>{service.title}</span>
                        <ArrowRight className="size-4 text-primary opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-[opacity,transform] duration-200" />
                      </Link>
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed mt-2.5">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="px-6 pb-6 pt-0">
                    <div className="pt-4 border-t border-border flex items-center gap-2 text-xs font-medium text-foreground">
                      <CheckCircle2 className="size-4 text-primary shrink-0" />
                      <span>{service.highlight}</span>
                    </div>
                  </CardContent>
                </div>

                <div className="px-6 pb-6 pt-0">
                  <Button asChild variant="outline" className="w-full justify-between font-medium group hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
                    <Link to={service.to}>
                      <span>Explore service details</span>
                      <ArrowRight className="size-4 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                  </Button>
                </div>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom Banner with Consultation CTA */}
        <motion.div initial={prefersReducedMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        delay: prefersReducedMotion ? 0 : 0.3
      }} className="rounded-2xl border border-border bg-secondary/60 p-8 sm:p-10 lg:p-12 text-center max-w-4xl mx-auto shadow-sm">
          <div className="inline-flex items-center justify-center size-12 rounded-full bg-primary/10 text-primary mb-5">
            <UserCheck className="size-6" />
          </div>
          <h3 className="text-2xl sm:text-3xl font-serif font-bold text-foreground mb-3">
            Not sure which engagement fits your goals?
          </h3>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
            Let us review your current search landscape, technical hurdles, and commercial targets together on a free 30-minute discovery call.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto px-8 font-semibold shadow-sm">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                <span>Book a Free SEO Consultation</span>
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border hover:bg-background/80">
              <Link to="/CaseStudiesPage" className="inline-flex items-center gap-2">
                <Sparkles className="size-4" />
                <span>Explore My Case Studies</span>
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}